// Updated RegisterationMain.js
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import './tabs/register.css'
import AmasiconRegistration from './tabs/TicketsRegister'
import {
  GetEventRegistration,
  EventRegister,
  EventRegisterPay,
  UpdatePaymentStatus,
  BuyerUpdate,
} from '../../../api/Webservices'
import { useParams } from 'react-router-dom'
import BuyerDetails from './tabs/BuyerDetails'
import AttendeeDetails from './tabs/AttendeeDetails'
import OrderSummary from './tabs/OrderSummary'
import SuccessPage from './RegistrationSuccessPage'
import { useRegistration } from '../../../context/RegistrationContext'

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

const Modal = React.memo(({ isOpen, onClose, message, isSuccess }) => {
  if (!isOpen) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className={`modal-header ${isSuccess ? 'success' : 'error'}`}>
          <h3>{isSuccess ? 'Success!' : 'Error'}</h3>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  )
})

const TraditionalLoader = () => (
  <div className="traditional-loader-container">
    <div className="traditional-loader"></div>
    <style>
      {`
        .traditional-loader-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
          min-height: 200px;
        }
        .traditional-loader {
          width: 40px;
          height: 40px;
          border: 4px solid #e5e7eb;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
)

const RegistrationMain = () => {
  const [eventData, setEventData] = useState(null)
  const [selectedTickets, setSelectedTickets] = useState([])
  const [currentStep, setCurrentStep] = useState('registration')
  const [buyerData, setBuyerData] = useState(null)
  const [attendeeData, setAttendeeData] = useState(null)
  const [successData, setSuccessData] = useState(null)
  const [completeEventData, setCompleteEventData] = useState([])
  const stateIsSame = completeEventData?.state_is_same === 'yes'
  const [modalState, setModalState] = useState({
    isOpen: false,
    message: '',
    isSuccess: false,
  })
  const [isTransitioning, setIsTransitioning] = useState(false)
  const { updateRegistrationData, clearRegistrationData, registrationData } =
    useRegistration()
  const { eventSlug, token } = useParams()
  const draftKey = `buyerDraft_${eventSlug}`

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const res = await GetEventRegistration(eventSlug, token || undefined)
        const cloned = JSON.parse(JSON.stringify(res))

        if (Array.isArray(cloned.tickets)) {
          cloned.tickets = cloned.tickets.map((ticket) => {
            if (ticket.variable_price_status && ticket.current_price != null) {
              return {
                ...ticket,
                price: ticket.current_price,
              }
            }
            return ticket
          })
        }

        setEventData(cloned)
        setCompleteEventData(cloned)
        localStorage.setItem('eventId', cloned.event.id)
      } catch (err) {
        console.error('Error fetching event data:', err)
        setModalState({
          isOpen: true,
          message: 'Failed to fetch event data. Please try again.',
          isSuccess: false,
        })
      }
    }

    fetchTickets()
    loadRazorpayScript().then((loaded) => {
      if (!loaded) {
        console.error('Failed to load Razorpay script')
        setModalState({
          isOpen: true,
          message: 'Failed to load payment gateway. Please try again.',
          isSuccess: false,
        })
      }
    })
  }, [eventSlug, token])

  useEffect(() => {
    if (buyerData && currentStep !== 'success') {
      const draft = { buyerData, currentStep }
      localStorage.setItem(draftKey, JSON.stringify(draft))
    }
  }, [buyerData, currentStep, draftKey])

  const handleTicketSelection = useCallback(
    (selectedTickets, nextStep, attendeesPaymentsId) => {
      setSelectedTickets(selectedTickets)
      const totalTickets = selectedTickets.reduce(
        (sum, ticket) =>
          sum + (ticket.ticketQuantity || 1) + (ticket.guestCount || 0),
        0
      )
      const allowBuyers = eventData?.settings?.allow_buyers === 1

      setIsTransitioning(true)
      setTimeout(() => {
        if (allowBuyers && totalTickets >= 2) {
          setCurrentStep('buyer-details')
        } else {
          setCurrentStep(nextStep)
        }
        setIsTransitioning(false)
      }, 1000)
    },
    [eventData]
  )

  const handleBuyerSubmit = useCallback(
    async (buyerFormData, nextStep) => {
      setBuyerData(buyerFormData)
      try {
        const buyerPayload = {
          id: registrationData.attendees_payments_id,
          event_id:
            localStorage.getItem('eventId') || eventData?.event?.id || 1,
          buyer_details: buyerFormData,
          first_name:
            buyerFormData.custom_fields.find((f) => f.name === 'first_name')
              ?.value || '',
          email:
            buyerFormData.custom_fields.find((f) => f.type === 'email')
              ?.value || '',
        }
        await BuyerUpdate(buyerPayload)
      } catch (err) {
        console.error('Error saving buyer data:', err)
        setModalState({
          isOpen: true,
          message: 'Failed to save buyer data. Please try again.',
          isSuccess: false,
        })
      }

      setIsTransitioning(true)
      setTimeout(() => {
        setCurrentStep(nextStep)
        setIsTransitioning(false)
      }, 1000)
    },
    [eventData, registrationData]
  )

  const handleAttendeeSubmit = useCallback((attendeeFormData, nextStep) => {
    setAttendeeData(attendeeFormData)
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentStep(nextStep)
      setIsTransitioning(false)
    }, 1000)
  }, [])

  const handleBackToAttendees = useCallback(() => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentStep('attendees')
      setIsTransitioning(false)
    }, 1000)
  }, [])

  const handleBackToTickets = useCallback(() => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentStep('registration')
      setIsTransitioning(false)
    }, 1000)
  }, [])

  const prepareSuccessData = useCallback(
    (orderData, paymentResponse) => {
      const attendees = []
      const guests = []

      if (attendeeData && attendeeData.attendee_data) {
        attendeeData.attendee_data.forEach((attendee) => {
          attendees.push({
            id: attendee.form_id,
            fullName: `${attendee.first_name} ${attendee.last_name}`,
            email: attendee.email,
          })

          if (attendee.guest_data) {
            attendee.guest_data.forEach((guest, index) => {
              const guestFields = guest.custom_fields || []
              const firstName =
                guestFields.find((f) => f.name === 'first_name')?.value ||
                'Guest'
              const lastName =
                guestFields.find((f) => f.name === 'last_name')?.value || ''
              const email =
                guestFields.find((f) => f.type === 'email')?.value || ''

              guests.push({
                id: `guest-${attendee.form_id}-${index}`,
                guestName: `${firstName} ${lastName}`.trim(),
                email: email,
              })
            })
          }
        })
      }

      const isFree = Number.parseFloat(orderData.total_amount || 0) === 0
      return {
        eventName: eventData?.event?.event_name || 'Event Registration',
        eventDate:
          eventData?.event?.event_start_date && eventData?.event?.event_end_date
            ? `${new Date(
                eventData.event.event_start_date
              ).toLocaleDateString()} - ${new Date(
                eventData.event.event_end_date
              ).toLocaleDateString()}`
            : 'TBD',
        venue: eventData?.event?.event_location || 'TBD',
        confirmationSent: true,
        attendees,
        guests,
        paymentDetails: {
          paymentDate: new Date().toLocaleDateString(),
          paymentTime: new Date().toLocaleTimeString(),
          amountPaid: `₹${orderData.total_amount || '0.00'}`,
          currency: orderData.currency || 'INR',
          buyerName:
            buyerData?.custom_fields?.find((f) => f.name === 'first_name')
              ?.value ||
            attendeeData?.attendee_data?.[0]?.first_name ||
            'Guest User',
          paymentMethod: isFree ? 'Free Registration' : 'Online - Razorpay',
          transactionId: isFree
            ? 'FREE_REGISTRATION'
            : paymentResponse?.razorpay_payment_id || '',
        },
      }
    },
    [eventData, attendeeData, buyerData]
  )
  const handleProceedToPayment = useCallback(
    async (orderData) => {
      setIsTransitioning(true)
      const { attendees_payments_id } = registrationData || {}

      if (!attendees_payments_id) {
        console.error('registrationData content:', registrationData)
        setModalState({
          isOpen: true,
          message:
            'Payment ID not found. Please restart the registration process.',
          isSuccess: false,
        })
        setIsTransitioning(false)
        throw new Error('attendees_payments_id not found in registration data')
      }

      const totalAmount = Number.parseFloat(orderData.total_amount || 0)
      const isFree = totalAmount === 0

      const finalPayload = {
        event_id: eventData?.event?.id || 1,
        tickets: attendeeData,
        buyer_details: buyerData || {
          buyer_type: 'individual',
          custom_fields: [],
        },
        ticket_count: selectedTickets.reduce(
          (sum, ticket) =>
            sum + (ticket.ticketQuantity || 1) + (ticket.guestCount || 0),
          0
        ),
        payment_date: new Date().toISOString().slice(0, 19).replace('T', ' '),
        payment_method: isFree ? 'Free Registration' : 'Razorpay',
        payment_received: orderData.total_amount || '0.00',
        additional_notes: '',
        total_amount: orderData.total_amount || '0.00',
        currency: selectedTickets[0]?.currency || 'INR',
        total_addons_amount: orderData.total_addons_amount || '0.00',
        total_tax_amount: orderData.total_tax_amount || '0.00',
        total_ticket_amount: orderData.total_ticket_amount || '0.00',
      }

      const paymentPayload = {
        event_id: eventData?.event?.id || 1,
        currency: selectedTickets[0]?.currency || 'INR',
        total_amount: totalAmount,
        ticket_quantity: finalPayload.ticket_count || 1,
        attendees_payments_id: registrationData.attendees_payments_id,
        payment_method: isFree ? 'Free Registration' : 'Razorpay',
        coupon_applied_amount: orderData.coupon_discount_amount || '0.00',
        coupon_applied_percentage: orderData.coupon_discount_percentage || 0,
        total_addons_amount: orderData.total_addons_amount || 0,
      }

      try {
        let paymentResponse

        const buyer = attendeeData?.buyer
        const firstAttendee = attendeeData?.tickets?.[0]?.attendee_data?.[0]

        const buyerName = `${buyer?.first_name || ''} ${
          buyer?.last_name || ''
        }`.trim()
        const attendeeName = `${firstAttendee?.first_name || ''} ${
          firstAttendee?.last_name || ''
        }`.trim()
        const name = buyerName || attendeeName || ''

        const email = buyer?.email || firstAttendee?.email || ''

        const phoneCustomField =
          firstAttendee?.form_data?.[0]?.custom_fields?.find(
            (field) => field.label === 'Phone'
          ) || {}
        const contact = phoneCustomField.value || ''

        const refetchAndMergeTickets = async (response) => {
          try {
            const updatedEventRes = await GetEventRegistration(
              eventSlug,
              token || undefined
            )
            const freshData = JSON.parse(JSON.stringify(updatedEventRes))
            const freshTickets = freshData.tickets || []

            const mergedTickets = freshTickets.map((freshTicket) => {
              const selected = selectedTickets.find(
                (s) => s.id === freshTicket.id
              )
              const isSoldOut = freshTicket.balance === 0

              return {
                ...freshTicket,
                ticketQuantity: isSoldOut ? 0 : selected?.ticketQuantity || 0,
                guestCount: isSoldOut ? 0 : selected?.guestCount || 0,
                isAdded: isSoldOut ? false : selected?.isAdded || false,
                ...(freshTicket.variable_price_status &&
                freshTicket.current_price != null
                  ? { price: freshTicket.current_price }
                  : {}),
              }
            })

            setEventData((prev) => ({ ...freshData, tickets: mergedTickets }))
            setCompleteEventData((prev) => ({
              ...freshData,
              tickets: mergedTickets,
            }))

            const stillAvailable = selectedTickets.filter((s) =>
              mergedTickets.some((t) => t.id === s.id && t.balance > 0)
            )
            setSelectedTickets(stillAvailable)

            const successPageData = prepareSuccessData(orderData, response)
            setSuccessData(successPageData)

            setTimeout(() => {
              setCurrentStep('success')
              setIsTransitioning(false)
              localStorage.removeItem(draftKey)
              clearRegistrationData()
            }, 1000)
          } catch (err) {
            console.error('Failed to refetch event data after payment:', err)
            const successPageData = prepareSuccessData(orderData, response)
            setSuccessData(successPageData)
            setTimeout(() => {
              setCurrentStep('success')
              setIsTransitioning(false)
              localStorage.removeItem(draftKey)
              clearRegistrationData()
            }, 1000)
          }
        }

        if (isFree) {
          const freePaymentStatusPayload = {
            attendees_payments_id,
            event_id: eventData?.event?.id || 1,
            payment_status: 'Success',
            payment_id: '-',
            payment_json: JSON.stringify({
              razorpay_payment_id: 'FREE_REGISTRATION',
            }),
          }
          await UpdatePaymentStatus(freePaymentStatusPayload)
          paymentResponse = { razorpay_payment_id: 'FREE_REGISTRATION' }

          await refetchAndMergeTickets(paymentResponse)
        } else {
          paymentResponse = await EventRegisterPay(paymentPayload)
          console.log(attendeeData, 'paymentPayload')

          if (paymentResponse.success) {
            const eventName =
              eventData?.event?.event_name || 'Event Registration'
            const eventDate = new Date(
              eventData?.event?.event_date || eventData?.event?.start_date
            ).toLocaleDateString()
            const ticketDetails = selectedTickets
              .map(
                (ticket) => `${ticket.title} (Qty: ${ticket.ticketQuantity})`
              )
              .join(', ')
            const detailedDescription = `Registration for ${eventName} on ${eventDate} - ${ticketDetails}`
            console.log(detailedDescription, 'detailedDescription')
            console.log(
              eventName,
              eventDate,
              ticketDetails,
              orderData.total_amount,
              "detailedDescription1"
            )
            const options = {
              key: eventData?.event_configuration.payment_key,
              amount: paymentResponse.amount * 100,
              currency: paymentResponse.currency,
              name: eventName,
              description: detailedDescription,
              order_id: paymentResponse.id,
              notes: {
                name:name,
                email:email,
                contact:contact,
                event_name: eventName,
                event_date: eventDate,
                tickets: ticketDetails,
                total_amount: orderData.total_amount,
              },

              handler: async (response) => {
                try {
                  const paymentStatusPayload = {
                    attendees_payments_id,
                    event_id: eventData?.event?.id || 1,
                    payment_status: response.razorpay_payment_id
                      ? 'Success'
                      : 'failed',
                    payment_id: response.razorpay_payment_id || '',
                    payment_json: JSON.stringify({
                      razorpay_order_id: response.razorpay_order_id,
                      razorpay_payment_id: response.razorpay_payment_id,
                      razorpay_signature: response.razorpay_signature,
                    }),
                  }
                  await UpdatePaymentStatus(paymentStatusPayload)

                  await refetchAndMergeTickets(response)
                } catch (err) {
                  console.error('Payment status update error:', err)
                  setModalState({
                    isOpen: true,
                    message:
                      'Payment completed but failed to update status. Please contact support.',
                    isSuccess: false,
                  })
                  setIsTransitioning(false)
                }
              },
              prefill: {
                name,
                email,
                contact,
              },

              theme: { color: '#F59E0B' },
              modal: {
                ondismiss: async () => {
                  try {
                    const paymentStatusPayload = {
                      attendees_payments_id,
                      event_id: eventData?.event?.id || 1,
                      payment_status: 'failed',
                      payment_id: '',
                      payment_json: '',
                    }
                    await UpdatePaymentStatus(paymentStatusPayload)
                    setModalState({
                      isOpen: true,
                      message:
                        'Payment was cancelled. You can try again or contact support if you need assistance.',
                      isSuccess: false,
                    })
                    setIsTransitioning(false)
                  } catch (err) {
                    console.error(
                      'Failed to update payment status on dismiss:',
                      err
                    )
                    setModalState({
                      isOpen: true,
                      message:
                        'Payment was cancelled and failed to update status. Please contact support.',
                      isSuccess: false,
                    })
                    setIsTransitioning(false)
                  }
                },
              },
            }
            const rzp = new window.Razorpay(options)
            rzp.open()
            return
          } else {
            throw new Error('Failed to initiate payment')
          }
        }
      } catch (err) {
        console.error('Payment error:', err)
        setModalState({
          isOpen: true,
          message: isFree
            ? 'Failed to process free registration. Please try again.'
            : 'An error occurred during payment initiation. Please try again.',
          isSuccess: false,
        })
        setIsTransitioning(false)
      }
    },
    [
      eventData,
      registrationData,
      attendeeData,
      buyerData,
      selectedTickets,
      prepareSuccessData,
      clearRegistrationData,
      draftKey,
      eventSlug,
      token,
      setEventData,
      setCompleteEventData,
      setSelectedTickets,
    ]
  )

  const handleEditAttendee = useCallback(() => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentStep('attendees')
      setIsTransitioning(false)
    }, 1000)
  }, [])

  const handleBackToHome = useCallback(() => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentStep('registration')
      setSelectedTickets([])
      setBuyerData(null)
      setAttendeeData(null)
      setSuccessData(null)
      localStorage.removeItem(draftKey)
      clearRegistrationData()
      setIsTransitioning(false)
    }, 1000)
  }, [clearRegistrationData, draftKey])

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false, message: '', isSuccess: false })
  }, [])

  const theme = useMemo(
    () =>
      completeEventData?.event_pages?.custom_theme
        ? JSON.parse(completeEventData.event_pages.custom_theme)
        : {},
    [completeEventData]
  )

  const backgroundStyle = useMemo(() => {
    const isThemeVisible = theme?.show === 'yes'
    return isThemeVisible
      ? theme.type === 'gradient' && theme.gradient
        ? theme.gradient
        : theme.type === 'color' && theme.color
        ? theme.color
        : '#005eff'
      : '#005eff'
  }, [theme])

  const primaryColor = useMemo(
    () =>
      theme?.show === 'yes' && theme.primary_color
        ? theme.primary_color
        : '#FF5733',
    [theme]
  )
  const secondaryColor = useMemo(
    () => (theme?.show === 'yes' && theme.color ? theme.color : '#005eff'),
    [theme]
  )
  const gradientStart = useMemo(
    () =>
      theme?.show === 'yes' && theme.type === 'gradient'
        ? '#005eff'
        : secondaryColor,
    [theme, secondaryColor]
  )
  const gradientEnd = useMemo(
    () =>
      theme?.show === 'yes' && theme.type === 'gradient'
        ? '#ff0000'
        : primaryColor,
    [theme, primaryColor]
  )

  return (
    <div className="main_rgister_page">
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        message={modalState.message}
        isSuccess={modalState.isSuccess}
      />
      <div className="main-container">
        <div
          className="fixed-background"
          style={{ background: backgroundStyle }}
        >
          <div className="bg-decorations">
            <style>
              {`
                .decoration-circle-1, .decoration-circle-2, .decoration-circle-3 {
                  background: ${primaryColor};
                  opacity: 0.3;
                }
                .floating-shape-1, .floating-shape-2, .floating-shape-3 {
                  background: ${secondaryColor};
                  opacity: 0.4;
                }
                .triangle-1, .triangle-2 {
                  border-bottom-color: ${gradientStart};
                  opacity: 0.5;
                }
                .diamond-1, .diamond-2 {
                  background: ${gradientEnd};
                  opacity: 0.4;
                }
                .wavy-line-1 path {
                  stroke: ${primaryColor};
                  opacity: 0.8;
                }
                .wavy-line-2 path {
                  stroke: ${gradientEnd};
                  opacity: 0.7;
                }
                .particle-1, .particle-2, .particle-3, .particle-4, .particle-5 {
                  background: ${secondaryColor};
                  opacity: 0.6;
                }
                .gradient-orb-1, .gradient-orb-2, .gradient-orb-3 {
                  background: linear-gradient(45deg, ${gradientStart}, ${gradientEnd});
                  opacity: 0.5;
                }
                .polygon-1, .polygon-2 {
                  background: ${primaryColor};
                  opacity: 0.4;
                }
                .spiral-1, .spiral-2 {
                  border-color: ${gradientStart};
                  opacity: 0.5;
                }
                .star-1, .star-2, .star-3 {
                  background: ${gradientEnd};
                  opacity: 0.6;
                }
                .plus-1, .plus-2 {
                  background: ${secondaryColor};
                  opacity: 0.5;
                }
              `}
            </style>
            <div className="decoration-circle-1"></div>
            <div className="decoration-circle-2"></div>
            <div className="decoration-circle-3"></div>
            <div className="floating-shape-1"></div>
            <div className="floating-shape-2"></div>
            <div className="floating-shape-3"></div>
            <div className="triangle-1"></div>
            <div className="triangle-2"></div>
            <div className="diamond-1"></div>
            <div className="diamond-2"></div>
            <div className="wavy-line-1">
              <svg viewBox="0 0 30 240" className="w-full h-full">
                <path
                  d="M15 0 Q25 30 15 60 Q5 90 15 120 Q25 150 15 180 Q5 210 15 240"
                  strokeWidth="3"
                  fill="none"
                />
              </svg>
            </div>
            <div className="wavy-line-2">
              <svg viewBox="0 0 20 160" className="w-full h-full">
                <path
                  d="M10 0 Q15 20 10 40 Q5 60 10 80 Q15 100 10 120 Q5 140 10 160"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </div>
            <div className="particle-1"></div>
            <div className="particle-2"></div>
            <div className="particle-3"></div>
            <div className="particle-4"></div>
            <div className="particle-5"></div>
            <div className="gradient-orb-1"></div>
            <div className="gradient-orb-2"></div>
            <div className="gradient-orb-3"></div>
            <div className="polygon-1"></div>
            <div className="polygon-2"></div>
            <div className="spiral-1"></div>
            <div className="spiral-2"></div>
            <div className="star-1"></div>
            <div className="star-2"></div>
            <div className="star-3"></div>
            <div className="plus-1"></div>
            <div className="plus-2"></div>
          </div>
        </div>
        <div className="card-container">
          <div className="main-card">
            {isTransitioning ? (
              <TraditionalLoader />
            ) : currentStep === 'success' ? (
              <SuccessPage
                registrationData={successData}
                onBackToHome={handleBackToHome}
                completeEventData={completeEventData}
              />
            ) : currentStep === 'registration' ? (
              <AmasiconRegistration
                eventData={eventData}
                onProceed={handleTicketSelection}
                completeEventData={completeEventData}
              />
            ) : currentStep === 'buyer-details' ? (
              <BuyerDetails
                onProceed={handleBuyerSubmit}
                eventData={eventData}
                onBack={handleBackToTickets}
              />
            ) : currentStep === 'attendees' ? (
              <AttendeeDetails
                eventData={eventData}
                selectedTickets={selectedTickets}
                onProceed={handleAttendeeSubmit}
                onBack={() => {
                  const allowBuyers = eventData?.settings?.allow_buyers === 1
                  const totalTickets = selectedTickets.reduce(
                    (sum, ticket) =>
                      sum +
                      (ticket.ticketQuantity || 0) +
                      (ticket.guestCount || 0),
                    0
                  )
                  const hasMultipleTickets =
                    new Set(selectedTickets.map((ticket) => ticket.id)).size > 1
                  const hasMultipleAttendees = selectedTickets.some(
                    (ticket) =>
                      (ticket.ticketQuantity || 0) > 1 ||
                      (ticket.guestCount || 0) > 1
                  )

                  setIsTransitioning(true)
                  setTimeout(() => {
                    if (
                      allowBuyers &&
                      (hasMultipleTickets ||
                        hasMultipleAttendees ||
                        totalTickets > 1)
                    ) {
                      setCurrentStep('buyer-details')
                    } else {
                      setCurrentStep('registration')
                    }
                    setIsTransitioning(false)
                  }, 1000)
                }}
              />
            ) : currentStep === 'order-summary' ? (
              <OrderSummary
                selectedTickets={selectedTickets}
                attendeeData={attendeeData}
                buyerData={buyerData}
                eventConfig={eventData?.event_configuration}
                onBack={handleBackToAttendees}
                onProceedToPayment={handleProceedToPayment}
                onEditAttendee={handleEditAttendee}
                eventData={eventData}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegistrationMain
